import { useEffect, useLayoutEffect, useRef } from 'react';
import cx from 'classnames';

import { useIVSPlayer } from 'hooks';
import { Loader } from 'components';

import { useLiveEvent } from './LiveEvent.context';

import styles from './LiveEvent.Playback.module.scss';

export type PlaybackProps = {
  isRoomLoading: boolean;
  playbackUrl: string;
  active?: boolean;
  gestureCaptured?: boolean;
  isPassiveViewer?: boolean;
  onAutoplayBlocked?(): void;
};

const Playback = ({
  isRoomLoading = false,
  playbackUrl,
  active = false,
  gestureCaptured = false,
  isPassiveViewer = true,
  onAutoplayBlocked
}: PlaybackProps): JSX.Element => {
  const liveEvent = useLiveEvent();
  const { videoEl, play, isLoading } = useIVSPlayer({
    playbackUrl,
    retryOnPause: true,
    active,
    onAutoplayBlocked
  });

  const containerRef = useRef<HTMLDivElement>(null);

  // update PiP state based on browser event
  useLayoutEffect(() => {
    const handleOnEnterPiP = () => {
      liveEvent.enablePictureInPictureMode();
    };

    const handleOnLeavePiP = () => {
      liveEvent.disablePictureInPictureMode();
    };

    videoEl.current?.addEventListener('enterpictureinpicture', handleOnEnterPiP);
    videoEl.current?.addEventListener('leavepictureinpicture', handleOnLeavePiP);

    return () => {
      videoEl.current?.removeEventListener('enterpictureinpicture', handleOnEnterPiP);
      videoEl.current?.removeEventListener('leavepictureinpicture', handleOnLeavePiP);
    };
  }, []);

  // close PiP in case they are enabled while unmounting
  useEffect(
    () => () => {
      const isInPictureInPictureMode = !!document.pictureInPictureElement;

      if (isInPictureInPictureMode) liveEvent.disablePictureInPictureMode(true);
    },
    []
  );

  // close PiP|FS in case they get to stage
  useEffect(() => {
    if (!isPassiveViewer) {
      if (document.pictureInPictureElement) liveEvent.disablePictureInPictureMode(true);
    }
  }, [isPassiveViewer]);

  // resume blocked audios when user gesture captured
  useEffect(() => {
    if (active && gestureCaptured) {
      play();
    }
  }, [gestureCaptured]);

  return (
    <div className={cx(styles.wrapper, { hidden: !active })}>
      <div ref={containerRef} className={styles.container}>
        {!isRoomLoading && isLoading && <Loader />}
        <video id="live_playback" muted playsInline ref={videoEl} controls controlsList="noplaybackrate nodownload" />
      </div>
    </div>
  );
};

export default Playback;
